import React, { useEffect, useRef, useState } from "react";
import { FaWhatsapp, FaFacebookMessenger, FaInstagram } from "react-icons/fa";

import FacebookSDK from "./FacebookSDK";
import getIntegrations from "./api/getIntegrations";
import handleFBMessenger from "./api/fbMessenger";
import { toast } from "react-toastify";
import handleInstaMessenger from "./api/instaMessenger";

const Integrate = ({ setActiveIntegration, userId, botId }) => {
  const [process, setProcess] = useState(false);
  const [allBotIntegration, setAllBotIntegration] = useState([]);
  const configId = "2156349601471542";

  const authCodeRef = useRef(null);
  const phoneNumberIdRef = useRef(null);
  const wabaIdRef = useRef(null);

  const tryTriggerAPI = async () => {
    const code = authCodeRef.current;
    const phoneNumberId = phoneNumberIdRef.current;
    const wabaId = wabaIdRef.current;

    if (!code || !phoneNumberId || !wabaId) return;

    try {
      const domain = "https://auth.jaano.ai";
      // const domain = "http://localhost:8000";
      const uri = `${domain}/int/handleAuthCode`;

      const res = await fetch(uri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          bot_id: botId,
          user_id: userId,
          phone_number_id: phoneNumberId,
          waba_id: wabaId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unknown error");
      setProcess(true);
      toast.success("WhatsApp Business Setup Successfully Completed");
      console.log("Backend Response:", data);
    } catch (err) {
      console.error("API call failed:", err);
      toast.error("Failed to complete WhatsApp Setup. Please try again.");
    }
  };

  useEffect(() => {
    const messageHandler = async (event) => {
      const validOrigins = [
        "https://www.facebook.com",
        "https://web.facebook.com",
      ];
      if (!validOrigins.includes(event.origin)) return;

      try {
        const eventData =
          typeof event?.data === "string" ? JSON.parse(event.data) : event.data;

        if (eventData?.type !== "WA_EMBEDDED_SIGNUP") return;

        const eventType = eventData?.event?.toUpperCase();

        if (eventType === "FINISH") {
          const { phone_number_id, waba_id } = eventData.data;
          phoneNumberIdRef.current = phone_number_id;
          wabaIdRef.current = waba_id;

          await tryTriggerAPI();
        }

        if (eventType === "CANCEL") {
          toast.error("WhatsApp Business Setup Cancelled");
        }
      } catch (err) {
        console.error("Error handling FB message:", err);
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, [botId, userId]);

  const handleWABA = () => {
    const fbLoginCallback = (response) => {
      if (!response.authResponse) {
        console.error("Login failed:", response);
        toast.error("Facebook login failed");
        return;
      }

      console.log("FB login successful:", response);
      authCodeRef.current = response.authResponse.code;
      tryTriggerAPI();
    };

    window.FB.login(fbLoginCallback, {
      config_id: configId,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: "",
        sessionInfoVersion: "2",
      },
    });
  };

  useEffect(() => {
    const fetchIntegrations = async () => {
      const res = await getIntegrations(botId);
      if (!res.error) {
        setAllBotIntegration(res.data);
      }
    };

    fetchIntegrations();
  }, [botId, process]);

  return (
    <div className="md:p-6 text-gray-900 dark:text-white">
      <FacebookSDK />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="flex flex-col justify-between p-6 shadow-xl bg-white dark:bg-gray-800 rounded-xl transition-transform hover:scale-105">
          <div>
            <FaWhatsapp className="w-16 h-16 text-green-500" />
            <h2 className="text-xl font-semibold mt-4">WhatsApp</h2>
            <p className=" text-gray-700 dark:text-gray-300">
              Connect your chatbot to a WhatsApp number and let it respond to
              customer messages.
            </p>
          </div>
          <button
            onClick={
              allBotIntegration.includes("whatsapp")
                ? () =>
                    setActiveIntegration({ status: true, event: "whatsapp" })
                : handleWABA
            }
            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all">
            {allBotIntegration.includes("whatsapp") ? "Manage" : "Connect"}
          </button>
        </div>
        <div className="relative group flex flex-col justify-between p-6 shadow-xl bg-white dark:bg-gray-800 rounded-xl transition-transform hover:scale-105">
          <div>
            <FaFacebookMessenger className="w-16 h-16 text-blue-500" />
            <h2 className="text-xl font-semibold mt-4">Facebook Messenger</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Connect your agent to a Facebook page and let it respond to
              messages from your customers.
            </p>
          </div>

          <button
            onClick={
              allBotIntegration.includes("fbmessenger")
                ? () =>
                    setActiveIntegration({ status: true, event: "fbmessenger" })
                : () => handleFBMessenger(botId, userId, setProcess)
            }
            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all z-10">
            {allBotIntegration.includes("fbmessenger") ? "Manage" : "Connect"}
          </button>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-70 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
            <span className="text-white text-lg font-bold">Coming Soon</span>
          </div>
        </div>

        <div className="relative group flex flex-col justify-between p-6 shadow-xl bg-white dark:bg-gray-800 rounded-xl transition-transform hover:scale-105">
          <div>
            <FaInstagram className="w-16 h-16 text-pink-600" />
            <h2 className="text-xl font-semibold mt-4">Instagram</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Connect your agent to an Instagram page and let it respond to
              messages from your customers.
            </p>
          </div>

          <button
            onClick={
              allBotIntegration.includes("instagram")
                ? () =>
                    setActiveIntegration({ status: true, event: "instagram" })
                : () => handleInstaMessenger(botId, userId, setProcess)
            }
            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all z-10">
            {allBotIntegration.includes("instagram") ? "Manage" : "Connect"}
          </button>

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black bg-opacity-70 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
            <span className="text-white text-lg font-bold">Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integrate;
